import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";

import Filter from "./components/Filter";
import QuickFilter from "./components/QuickFilter";
import NotificationTable from "./components/NotificationTable";

import { CHANNEL_OPTIONS, QUICK_FILTER } from "./constant";
import { getCall, postCall } from "services";
import { sentenceCase } from "utils";
import { formatDate } from "./makeData";

const LIMIT = 10;
const DAYS_30 = 30 * 24 * 60 * 60 * 1000;
const CACHE_DURATION = 5000; // 5 seconds cache

const NotificationManagement = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [notificationData, setNotificationData] = useState([]);
  const [filters, setFilters] = useState({
    quickFilter: QUICK_FILTER.All,
    channel: "EMAIL",
    toDate: formatDate(new Date()),
    fromDate: formatDate(new Date(Date.now() - DAYS_30)),
  });

  const [filterData, setFilterData] = useState({
    templates: [],
    whatsappStatuses: [],
    emailStatuses: [],
    notificationProviderStatuses: [],
  });

  // Refs for request cancellation and caching
  const abortControllerRef = useRef(null);
  const requestCacheRef = useRef(new Map());
  const debounceTimerRef = useRef(null);

  const payload = useMemo(
    () => ({
      cursor: cursor,
      limit: LIMIT,
      filterDto: {
        event_id: filters.event_id,
        receiver: filters.receiver,
        provider_status: filters.provider_status,
        template_type: filters.template_type,
        toDate: formatDate(filters.toDate),
        fromDate: formatDate(filters.fromDate),
        reference: filters.reference,
        quickFilter: filters.quickFilter,
        channel: filters.channel,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageNumber, filters],
  );

  const fetchTemplateSummary = async () => {
    try {
      const res = await getCall(
        "joms/api/external/v1/notification/templates-summary",
      );
      const { data } = res;

      setFilterData({
        templates: data.templates || [],
        whatsappStatuses: data.whatsappStatuses || [],
        emailStatuses: data.emailStatuses || [],
        notificationProviderStatuses: data.notificationProviderStatuses || [],
      });
    } catch (err) {
      console.error("Error fetching template summary", err);
    }
  };

  const getProviderStatusOptions = (channel) => {
    if (channel === CHANNEL_OPTIONS.email) {
      return filterData.emailStatuses.map((status) => ({
        label: sentenceCase(status),
        value: status,
      }));
    }

    if (channel === CHANNEL_OPTIONS.Whatsapp) {
      return filterData.whatsappStatuses.map((status) => ({
        label: sentenceCase(status),
        value: status,
      }));
    }

    return [];
  };

  const fetchNotifications = async (payload) => {
    // Cancel previous request if pending
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // Check cache first
    const cacheKey = JSON.stringify(payload);
    const cached = requestCacheRef.current.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setNotificationData(cached.data);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const res = await postCall(
        "joms/api/external/v1/notification/get-notifications-status",
        payload,
      );

      // Only update state if request wasn't aborted
      if (!abortControllerRef.current.signal.aborted) {
        const data = res?.data || [];
        setCursor(data?.nextCursor || null);
        setNotificationData(data);
        // Cache the result
        requestCacheRef.current.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name !== "AbortError") {
        console.error("Error fetching notifications", err);
        setNotificationData([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitFilter = useCallback((payload) => {
    // Debounce filter submission to avoid rapid API calls
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setPageNumber(1);
      setCursor(null);
      setFilters((prev) => ({
        ...prev,
        ...payload,
      }));
    }, 1000);
  }, []);

  const handleResetFilter = useCallback(() => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setPageNumber(1);
      setCursor(null);
      setFilters({
        quickFilter: QUICK_FILTER.All,
        channel: "EMAIL",
        toDate: formatDate(new Date()),
        fromDate: formatDate(new Date(Date.now() - DAYS_30)),
      });
    }, 1000);
  }, []);

  const handleQuickFilter = useCallback((value) => {
    setPageNumber(1);
    setCursor(null);
    setFilters((prev) => ({
      ...prev,
      quickFilter: value,
    }));
  }, []);

  const handlePageChange = useCallback(
    (direction, page) => {
      setPageNumber(page);
      if (direction === "NEXT") {
        setCursor(notificationData.nextCursor);
      } else {
        setCursor(notificationData.previousCursor);
        if (page === 1) {
          setCursor(null);
        }
      }
    },
    [notificationData.nextCursor, notificationData.previousCursor],
  );

  useEffect(() => {
    void fetchTemplateSummary();
  }, []);

  useEffect(() => {
    setNotificationData([]);
    void fetchNotifications(payload);
  }, [payload]);

  // Cleanup: abort pending requests on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return (
    <Box
      sx={{
        p: 6,
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Typography variant="h2">Notification management</Typography>

      <Filter
        onSubmit={handleSubmitFilter}
        onReset={handleResetFilter}
        templates={filterData.templates}
        notificationProviderStatuses={filterData.notificationProviderStatuses}
        getProviderStatusOptions={getProviderStatusOptions}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        mt={4}
      >
        <QuickFilter
          state={filters.quickFilter}
          onQuickFilter={handleQuickFilter}
        />
      </Box>

      <Typography variant="h4">Notification service logs</Typography>

      <NotificationTable
        data={notificationData}
        onPageChange={handlePageChange}
        pageNumber={pageNumber}
        isNotificationDataLoading={isLoading}
        showAttachmentsForChannels={["EMAIL"]}
        limit={LIMIT}
      />
    </Box>
  );
};

export default NotificationManagement;
